import React, { useState } from "react";
import { Typography, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import BoxInventory from "../components/BoxInventory";
import ShipmentsTable from "../components/ShipmentsTable";
import PendingDevices from "../components/PendingDevices";
import CreateBoxOrderModal from "../components/CreateBoxOrderModal";
import ReceiveBoxConfirmModal from "../components/ReceiveBoxConfirmModal";
import StoreSelector from "../components/StoreSelector";
import { useStoreContext } from "../contexts/storeContext";
import usePendingDevices from "../hooks/usePendingDevices";
import useShippedDevices from "../hooks/useShippedDevices";
import useBoxOrders from "../hooks/useBoxOrders";
import useBoxInventory from "../hooks/useBoxInventory";
import useApi from "../hooks/useApi";
import { useUserContext } from "../contexts/userContext";
import toastr from "toastr";
import {
  showSuccessToast,
  SUCCESS_TOAST_MESSAGES,
} from "../utils/showSuccessToast";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    color: "#212529",
    fontSize: 24,
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    letterSpacing: "-0.24px",
  },
}));

const Shipments = ({ onTabChange }) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width:769px)");
  const classes = useStyles(isDesktop);

  const { selectedStore } = useStoreContext();
  const { user } = useUserContext();

  // Page size states for different tables
  const [boxInventoryPageSize, setBoxInventoryPageSize] = useState(10);
  const [boxOrdersPageSize, setBoxOrdersPageSize] = useState(10);
  const [pendingDevicesPageSize, setPendingDevicesPageSize] = useState(10);
  const [shippedDevicesPageSize, setShippedDevicesPageSize] = useState(10);

  // Page states
  const [boxInventoryPage, setBoxInventoryPage] = useState(1);
  const [boxOrdersPage, setBoxOrdersPage] = useState(1);
  const [pendingDevicesPage, setPendingDevicesPage] = useState(1);
  const [shippedDevicesPage, setShippedDevicesPage] = useState(1);

  const {
    inventory,
    isLoading: inventoryLoading,
    error: inventoryError,
    refetch: refetchBoxInventory,
  } = useBoxInventory({
    storeId: selectedStore?.id,
    page: boxInventoryPage,
    pageSize: boxInventoryPageSize,
  });


  const { request } = useApi();
  const {
    orders: boxOrders,
    isLoading: boxOrdersLoading,
    error: boxOrdersError,
    total: boxOrdersTotal,
    refetch: refetchBoxOrders,
  } = useBoxOrders({
    storeId: selectedStore?.id,
    receivedStatus: "PENDING",
    page: boxOrdersPage,
    pageSize: boxOrdersPageSize,
  });
  const {
    pendingDevices,
    isLoading: pendingLoading,
    refetch: refetchPendingDevices,
    removeByIds,
    total: pendingDevicesTotal,
  } = usePendingDevices({
    storeId: selectedStore?.id,
    page: pendingDevicesPage,
    pageSize: pendingDevicesPageSize,
  });

  const {
    shippedDevices,
    isLoading: shippedLoading,
    refetch: refetchShippedDevices,
    total: shippedDevicesTotal,
  } = useShippedDevices({
    storeId: selectedStore?.id,
    page: shippedDevicesPage,
    pageSize: shippedDevicesPageSize,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [pendingReceiveShipment, setPendingReceiveShipment] = useState(null);
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);

  const handleLocationChange = () => {
    // Handle location selector click
    console.log("Location selector clicked");
  };

  const handleCreateBoxOrder = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCreateOrder = (orderData) => {
    // Handle create order with boxType and quantity
    console.log("Create order:", orderData);
    // TODO: Implement API call to create box order
  };

  const applyToastrOptions = () => {
    toastr.options = {
      closeButton: true,
      newestOnTop: true,
      progressBar: false,
      preventDuplicates: false,
      positionClass: "toast-top-center",
      timeOut: "5000",
      hideEasing: "linear",
      showMethod: "fadeIn",
      hideMethod: "fadeOut",
      preventDuplicates: true,
    };
  };

  /** @returns {Promise<boolean>} */
  const executeReceive = async (shipment) => {
    if (!shipment?.id) {
      console.error("Shipment ID is missing");
      return false;
    }

    const storeId = user?.store?.id;
    if (!storeId) {
      console.error("Store ID is missing");
      return false;
    }

    try {
      await request({
        path: `/box/order/${shipment.id}/receive`,
        method: "PATCH",
        headers: { "X-Store-Id": storeId },
      });

      showSuccessToast(SUCCESS_TOAST_MESSAGES.boxReceived);

      if (refetchBoxOrders) {
        refetchBoxOrders();
      }
      return true;
    } catch (error) {
      console.error("Failed to receive box order:", error);
      applyToastrOptions();
      const errorMessage =
        error?.message || "Failed to receive box order. Please try again.";
      toastr.error(errorMessage);
      return false;
    }
  };

  const handleReceiveClick = (shipment) => {
    setPendingReceiveShipment(shipment);
    setReceiveModalOpen(true);
  };

  const handleCloseReceiveModal = () => {
    if (receiveSubmitting) return;
    setReceiveModalOpen(false);
    setPendingReceiveShipment(null);
  };

  const handleConfirmReceive = async () => {
    setReceiveSubmitting(true);
    try {
      const ok = await executeReceive(pendingReceiveShipment);
      if (ok) {
        setReceiveModalOpen(false);
        setPendingReceiveShipment(null);
      }
    } finally {
      setReceiveSubmitting(false);
    }
  };

  const handleCreateShipment = (shipmentData) => {
    const ids = (shipmentData?.devices || [])
      .map((device) => device.id)
      .filter(Boolean);
    removeByIds(ids);
    refetchPendingDevices();
    refetchShippedDevices();
    // Refetch box inventory to update the quantity values
    if (refetchBoxInventory) {
      refetchBoxInventory();
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography className={classes.title}>{t("Shipments")}</Typography>
        <StoreSelector />
      </div>

      <BoxInventory
        inventory={inventory}
        onCreateBoxOrder={handleCreateBoxOrder}
      >
          <ShipmentsTable
            data={boxOrders}
            onReceive={handleReceiveClick}
            isLoading={boxOrdersLoading}
          />
      </BoxInventory>

      <PendingDevices
        data={pendingDevices}
        shippedData={shippedDevices.map((shipment) => ({
            id: shipment.id,
          shipmentNo: shipment.shipmentNo || "—",
          trackingNo: shipment.trackingNumber,
          trackingLink: shipment.trackingLink,
          created: shipment.createdAt,
          numDevices: shipment.shippedDevices?.length || 0,
          boxType: shipment.boxType,
          devices: shipment.shippedDevices || [],
            inboundShippingLabelUrl: shipment.inboundShippingLabelUrl,
        }))}
        pendingLoading={pendingLoading}
        shippedLoading={shippedLoading}
        onCreateShipment={handleCreateShipment}
        boxInventory={inventory}
        pendingPage={pendingDevicesPage}
        pendingPageSize={pendingDevicesPageSize}
        pendingTotal={pendingDevicesTotal}
        onPendingPageChange={(newPage) => setPendingDevicesPage(newPage)}
        onPendingPageSizeChange={(newPageSize) => {
          setPendingDevicesPageSize(newPageSize);
          setPendingDevicesPage(1); // Reset to first page when page size changes
        }}
        shippedPage={shippedDevicesPage}
        shippedPageSize={shippedDevicesPageSize}
        shippedTotal={shippedDevicesTotal}
        onShippedPageChange={(newPage) => setShippedDevicesPage(newPage)}
        onShippedPageSizeChange={(newPageSize) => {
          setShippedDevicesPageSize(newPageSize);
          setShippedDevicesPage(1); // Reset to first page when page size changes
        }}
      />

      <ReceiveBoxConfirmModal
        open={receiveModalOpen}
        onClose={handleCloseReceiveModal}
        onConfirm={handleConfirmReceive}
        loading={receiveSubmitting}
      />

      <CreateBoxOrderModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onCreateOrder={handleCreateOrder}
      />
    </div>
  );
};

export default Shipments;

